import { GetWishlistResponse, IWishlist } from "@app/types/wishlist"
import { fetchOnce } from "@app/utils/fetchOnce"

const store = window.Shopify.shop.replace(".myshopify.com", "")

let cachedWishlists: GetWishlistResponse["data"] | null = null

export const getWishlists = async (
  owner: string,
  allowCache = false
): Promise<GetWishlistResponse["data"] | null> => {
  if (allowCache && cachedWishlists) return cachedWishlists

  try {
    const res = (await fetchOnce(
      `https://wh-fe.au-aws.thewishlist.io/api/v1/store-front/wishlist/all?shop_hash=${store}&owner=${owner}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    )) as GetWishlistResponse

    // Check if the expected data structure is present
    if (res?.data) {
      cachedWishlists = res.data
      return res.data
    } else {
      console.error(
        "[wishlist] Fetch for wishlists did not return expected data structure"
      )
    }
  } catch (error) {
    console.error("Error:", error)
  }

  return null
}

export const getShareUrl = async (wishlist: IWishlist) => {
  try {
    const response = await fetch(
      `https://wh-fe.au-aws.thewishlist.io/api/v1/store-front/wishlist/share?shop_hash=${wishlist.shop_hash}&id=${wishlist.id}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    )

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    } else {
      const data = await response.json()
      return data?.data
        ? `${window.location.origin}/pages/shared-wishlist/?share_id=${data.data}`
        : ""
    }
  } catch (error) {
    console.error("Error:", error)
  }

  return ""
}

export const startNativeShare = async (url: string) => {
  if (navigator.share) {
    try {
      await navigator.share({
        url,
      })
      return true
    } catch (error) {
      console.error("Could not share", error)
    }
  }
}

export const renameWishlist = async (wishlist: IWishlist, title: string) => {
  try {
    const response = await fetch(
      `https://wh-fe.au-aws.thewishlist.io/api/v1/store-front/wishlist/${wishlist.id}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...wishlist, title }),
      }
    )

    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`)
      return false
    } else {
      return true
    }
  } catch (error) {
    console.error("Error:", error)
  }

  return false
}

export const deleteWishlist = async (wishlist: IWishlist) => {
  try {
    const response = await fetch(
      `https://wh-fe.au-aws.thewishlist.io/api/v1/store-front/wishlist/${wishlist.id}?shop_hash=${wishlist.shop_hash}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      }
    )

    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`)
      return false
    } else {
      return true
    }
  } catch (error) {
    console.error("Error:", error)
  }

  return false
}

export const createWishlist = async (
  owner: string,
  name?: string
): Promise<{ id?: string } | undefined> => {
  try {
    const response = await fetch(
      `
    https://wh-fe.au-aws.thewishlist.io/api/v1/store-front/wishlist`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          title: name || "New Wishlist",
          owner,
          shop_hash: store,
          is_default_wishlist: false,
          published: false,
          subscribe: true,
        }),
      }
    )

    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`)
    } else {
      const data = await response.json()
      return data?.data || {}
    }
  } catch (error) {
    console.error("Error:", error)
  }
}

export const createDefaultWishlist = async (
  owner: string
): Promise<{ id?: string } | undefined> => {
  try {
    const response = await fetch(
      `
    https://wh-fe.au-aws.thewishlist.io/api/v1/store-front/wishlist`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          title: "My Wishlist",
          owner,
          shop_hash: store,
          is_default_wishlist: true,
          published: false,
          subscribe: true,
        }),
      }
    )

    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`)
    } else {
      const data = await response.json()
      return data?.data || {}
    }
  } catch (error) {
    console.error("Error:", error)
  }
}

let cachedSharedWishlists: IWishlist | null = null

export const getWishlistByShareHash = async (
  shareHash: string,
  allowCache = false
): Promise<IWishlist | null> => {
  if (allowCache && cachedSharedWishlists) return cachedSharedWishlists

  try {
    const response = await fetch(
      `https://wh-fe.au-aws.thewishlist.io/api/v1/store-front/wishlist/share/detail?hash=${shareHash}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    )

    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`)
    } else {
      const data = await response.json()
      cachedSharedWishlists = data?.data || null
      return data?.data || null
    }
  } catch (error) {
    console.error("Error:", error)
  }

  return null
}

export const getProductDetails = async (productHandle: string) => {
  const url = `${window.Shopify.routes.root}products/${productHandle}.js`
  try {
    const response = await fetch(url)

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    } else {
      const data = await response.json()
      return data
    }
  } catch (error) {
    console.error("Error in fetching product details:", error)
  }
}

export const addProductToWishlist = async ({
  owner,
  product_id,
  variant_id,
  wishlist_ids,
}: {
  owner: string
  product_id: string
  variant_id: string
  wishlist_ids: string[]
}) => {
  try {
    const response = await fetch(
      `https://wh-fe.au-aws.thewishlist.io/api/v1/store-front/wishlist/add-product-to-wishlist`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          variant_id,
          wishlist_ids,
          product_id,
          shop_hash: store,
          owner,
        }),
      }
    )

    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`)
    } else {
      document.dispatchEvent(new Event("wishlist-items:update"))
      return true
    }
  } catch (error) {
    console.error("Error:", error)
  }

  return false
}

export const removeFromWishlist = async (
  item_id: string,
  product_id: string,
  variant_id: string,
  wishlist_id: string
) => {
  try {
    const response = await fetch(
      "https://wh-fe.au-aws.thewishlist.io/api/v1/store-front/wishlist/remove-product-in-wishlist",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          is_add_to_cart: false,
          item_id,
          product_id,
          shop_hash: store || "",
          variant_id,
          wishlist_id,
          wishlist_uuid: "",
        }),
      }
    )

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    document.dispatchEvent(new Event("wishlist-items:update"))
  } catch (error) {
    console.error("Error:", error)
  }
}

export const addToWishlistStorage = (productHandle: string) => {
  if (!sessionStorage) return
  sessionStorage.setItem("add-to-wishlist", productHandle)
}

export const getWishlistStorage = () => {
  if (!sessionStorage) return
  return sessionStorage.getItem("add-to-wishlist")
}

export const removeFromWishlistStorage = () => {
  if (!sessionStorage) return
  sessionStorage.removeItem("add-to-wishlist")
}
